import Controller from '@ember/controller';
import { action } from '@ember/object';
import { ajax } from 'discourse/lib/ajax';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';

// Several report fields are plain JSON objects keyed by an arbitrary string
// (e.g. { "US" => 40, "GB" => 12 }) rather than arrays — {{#each}} can't
// iterate those directly in a classic (non-strict-mode) template without the
// long-deprecated {{#each-in}} helper, so this converts them to
// [{key, count}] once instead.
function toEntries(obj) {
  if (!obj) return [];
  return Object.keys(obj)
    .map(key => ({ name: key, count: obj[key] }))
    .sort((a, b) => b.count - a.count);
}

const REPORT_ENDPOINTS = {
  pageviews: '/admin/plugins/proxyviews-analytics/pageviews',
  content: '/admin/plugins/proxyviews-analytics/content',
  affiliates: '/admin/plugins/proxyviews-analytics/affiliates',
  users: '/admin/plugins/proxyviews-analytics/users',
  funnels: '/admin/plugins/proxyviews-analytics/funnels',
  cohorts: '/admin/plugins/proxyviews-analytics/cohorts',
  experiments: '/admin/plugins/proxyviews-analytics/experiments',
};

// All-read-only reporting, same as affiliates: this is derived telemetry
// (page views, clicks, scroll depth) collected automatically from visitor
// activity, not admin-authored content, so there's nothing to create/edit
// here beyond the date-range filters each report already supports.
export default class AdminPluginsProxyviewsAnalyticsController extends Controller {
  @service dialog;
  @tracked activeTab = 'overview';
  @tracked reportData = {};
  @tracked reportLoading = false;
  @tracked message = null;
  @tracked loadedTabs = {};

  // For Experiments UI
  @tracked isEditingExperiment = false;
  @tracked editingExperiment = null;

  reportTabs = ['overview', 'pageviews', 'content', 'affiliates', 'users', 'funnels', 'cohorts', 'experiments'];

  @action
  async changeTab(tab) {
    this.activeTab = tab;
    if (tab === 'overview' || this.loadedTabs[tab]) {
      // Allow refreshing experiments
      if (tab !== 'experiments') return;
    }
    const endpoint = REPORT_ENDPOINTS[tab];
    if (!endpoint) {
      return;
    }
    this.reportLoading = true;
    try {
      const data = await ajax(endpoint);
      this.reportData = { ...this.reportData, [tab]: data };
      this.loadedTabs = { ...this.loadedTabs, [tab]: true };
    } catch (err) {
      this.message = { text: `Failed to load ${tab} report.`, type: 'danger' };
    } finally {
      this.reportLoading = false;
    }
  }

  @action
  editExperiment(experiment) {
    if (experiment) {
      this.editingExperiment = {
        ...experiment,
        variants: experiment.variants.map(v => ({...v, payload: JSON.stringify(v.payload || {})}))
      };
    } else {
      this.editingExperiment = { name: '', description: '', status: 'draft', traffic_allocation: 100, variants: [] };
    }
    this.isEditingExperiment = true;
  }

  @action
  cancelEditExperiment() {
    this.isEditingExperiment = false;
    this.editingExperiment = null;
  }

  @action
  setField(field, event) {
    this.editingExperiment[field] = event.target.value;
  }

  @action
  updateVariantField(index, field, event) {
    this.editingExperiment.variants[index][field] = event.target.value;
  }

  @action
  addVariant() {
    this.editingExperiment.variants.pushObject({ name: '', weight: 50, payload: '{}' });
  }

  @action
  removeVariant(index) {
    this.editingExperiment.variants.removeAt(index);
  }

  @action
  updateVariantPayload(index, event) {
    const value = event.target.value;
    const variants = this.editingExperiment.variants;
    variants[index].payload = value;
    this.editingExperiment.variants = [...variants];
  }

  @action
  async saveExperiment() {
    this.message = null;
    const isNew = !this.editingExperiment.id;
    const url = isNew 
      ? '/admin/plugins/proxyviews-analytics/experiments'
      : `/admin/plugins/proxyviews-analytics/experiments/${this.editingExperiment.id}`;
      
    // Parse JSON payloads
    const payloadVariants = this.editingExperiment.variants.map(v => {
      let parsedPayload = {};
      try {
        parsedPayload = JSON.parse(v.payload || '{}');
      } catch (e) {
        // Fallback or ignore invalid JSON for now
      }
      return { ...v, payload: parsedPayload };
    });

    const data = {
      name: this.editingExperiment.name,
      description: this.editingExperiment.description,
      status: this.editingExperiment.status,
      traffic_allocation: this.editingExperiment.traffic_allocation,
      variants: payloadVariants
    };

    try {
      await ajax(url, { type: isNew ? 'POST' : 'PUT', data });
      this.isEditingExperiment = false;
      this.message = { text: 'Experiment saved!', type: 'success' };
      this.changeTab('experiments'); // reload
    } catch (err) {
      const errorMsg = err.jqXHR?.responseJSON?.errors?.join(', ') || 'Failed to save experiment.';
      this.message = { text: errorMsg, type: 'danger' };
    }
  }

  @action
  async deleteExperiment(experiment) {
    this.dialog.yesNoConfirm({
      message: `Delete experiment ${experiment.name}?`,
      didConfirm: async () => {
        try {
          await ajax(`/admin/plugins/proxyviews-analytics/experiments/${experiment.id}`, { type: 'DELETE' });
          this.changeTab('experiments'); // reload
          this.message = { text: 'Experiment deleted!', type: 'success' };
        } catch (err) {
          this.message = { text: 'Failed to delete experiment.', type: 'danger' };
        }
      }
    });
  }

  get overview() {
    // changeTab stores fetched data into this.reportData[tab]; the old
    // @tracked overview field was a dead initializer that was never assigned,
    // so the overview tab rendered blank stat cards/tables forever.
    return this.reportData.overview || {};
  }

  get experimentsData() {
    return this.reportData.experiments?.experiments || [];
  }

  get pageviewsData() {
    return this.reportData.pageviews || {};
  }

  get contentData() {
    return this.reportData.content || {};
  }

  get affiliatesData() {
    return this.reportData.affiliates || {};
  }

  get usersData() {
    return this.reportData.users || {};
  }

  get funnelsData() {
    return this.reportData.funnels || {};
  }

  get cohortsData() {
    return this.reportData.cohorts || {};
  }

  get pageviewsSourceBreakdown() {
    return toEntries(this.pageviewsData.source_breakdown);
  }

  get contentViewsByType() {
    return toEntries(this.contentData.views_by_type);
  }

  get affiliatesClicksBySource() {
    return toEntries(this.affiliatesData.clicks_by_source);
  }

  get affiliatesCountryBreakdown() {
    return toEntries(this.affiliatesData.country_breakdown);
  }

  get usersTopSearches() {
    return toEntries(this.usersData.top_searches);
  }
}
